import { useState } from 'react';
import { Badge } from '../../../components/ui/Badge';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Tabs } from '../../../components/ui/Tabs';
import { StateBlock } from '../../../components/ui/Feedback';
import { VENDOR_TYPE_META } from '../../../lib/status';
import type { Vendor } from '../../../types/api';

type VendorTab = 'vendor-list' | 'others';

interface VendorStatusPanelProps {
  vendors: Vendor[];
  selectedVendorIds: Set<number>;
  onToggleVendor: (vendorId: number) => void;
  onToggleAllVendors: (checked: boolean) => void;
  title: string;
  onTitleChange: (value: string) => void;
  dueDate: string;
  onDueDateChange: (value: string) => void;
  notes: string;
  onNotesChange: (value: string) => void;
}

/**
 * Second panel of the review step: the "Vendors & Status" banner, the
 * "Vendor List" / "Others" tabs and the selectable vendor table.
 * Screens RFP 04 (Vendor Type badges).
 */
export function VendorStatusPanel({
  vendors,
  selectedVendorIds,
  onToggleVendor,
  onToggleAllVendors,
  title,
  onTitleChange,
  dueDate,
  onDueDateChange,
  notes,
  onNotesChange,
}: VendorStatusPanelProps) {
  const [tab, setTab] = useState<VendorTab>('vendor-list');

  const allSelected = vendors.length > 0 && vendors.every((vendor) => selectedVendorIds.has(vendor.id));
  const someSelected = vendors.some((vendor) => selectedVendorIds.has(vendor.id));

  return (
    <section className="panel">
      <div className="review-section">
        <div className="section-banner">Vendors &amp; Status</div>

        <div className="review-section__body">
          <Tabs
            tabs={[
              { value: 'vendor-list' as VendorTab, label: 'Vendor List' },
              { value: 'others' as VendorTab, label: 'Others' },
            ]}
            value={tab}
            onChange={setTab}
          />

          {tab === 'vendor-list' ? (
            <div className="table-wrap review-section__table">
              <table className="data-table vendor-review-table">
                <thead>
                  <tr>
                    <th className="cell-center">
                      <Checkbox
                        label="Select all vendors"
                        checked={allSelected}
                        indeterminate={!allSelected && someSelected}
                        onChange={(event) => onToggleAllVendors(event.target.checked)}
                      />
                    </th>
                    <th style={{ width: '26%' }}>Vendor Name</th>
                    <th style={{ width: '14%' }}>Vendor Type</th>
                    <th style={{ width: '20%' }}>Contact Person</th>
                    <th style={{ width: '18%' }}>Contact Number</th>
                    <th style={{ width: '22%' }}>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        <StateBlock title="No vendors available" />
                      </td>
                    </tr>
                  ) : null}

                  {vendors.map((vendor) => {
                    const typeMeta = VENDOR_TYPE_META[vendor.vendorType];
                    return (
                      <tr key={vendor.id}>
                        <td className="cell-center">
                          <Checkbox
                            label={`Select ${vendor.name}`}
                            checked={selectedVendorIds.has(vendor.id)}
                            onChange={() => onToggleVendor(vendor.id)}
                          />
                        </td>
                        <td>{vendor.name}</td>
                        <td>
                          <Badge variant={typeMeta.variant}>{typeMeta.label}</Badge>
                        </td>
                        <td>{vendor.contactPerson ?? '—'}</td>
                        <td>{vendor.contactNumber ?? '—'}</td>
                        <td>{vendor.email ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="review-grid review-grid--spaced">
              <label className="review-grid__label" htmlFor="rfp-title">
                RFP Title
              </label>
              <div className="review-grid__value">
                <input
                  id="rfp-title"
                  className="input"
                  value={title}
                  onChange={(event) => onTitleChange(event.target.value)}
                  placeholder="e.g. Annual laptop refresh"
                />
              </div>

              <label className="review-grid__label" htmlFor="rfp-due-date">
                Due Date
              </label>
              <div className="review-grid__value">
                <input
                  id="rfp-due-date"
                  type="date"
                  className="input"
                  value={dueDate}
                  onChange={(event) => onDueDateChange(event.target.value)}
                />
              </div>

              <label className="review-grid__label" htmlFor="rfp-notes">
                Notes
              </label>
              <div className="review-grid__value">
                <textarea
                  id="rfp-notes"
                  className="textarea"
                  value={notes}
                  onChange={(event) => onNotesChange(event.target.value)}
                  placeholder="Anything the vendors should know"
                />
              </div>
            </div>
          )}

          <p className="review-count">
            {selectedVendorIds.size} of {vendors.length} vendors selected
          </p>
        </div>
      </div>
    </section>
  );
}
